import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  NEVER,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { EmailAcountEditorModalComponent } from '../email-account-editor-modal';
import { ActivatedRoute, Params } from '@angular/router';
import { NotificationService } from 'src/app/shared/services';
import { LocationService } from 'src/app/core/services/global';
import {
  CampaignEmailCredential,
  EmailCredentialSummaryResponse,
} from 'src/app/shared/models/campaign/campaign-account';
import { CampaignService } from 'src/app/shared/services/requests/campaign.service';
import { NotificationTypeEnum } from 'src/app/shared/enums';
import { RemoveEntityModalComponent } from 'src/app/modules/system-shared/components/modals';
import { UpdateEntity } from 'src/app/shared/models';

@Component({
  selector: 'app-email-accounts',
  templateUrl: './email-accounts.component.html',
  styleUrls: ['./email-accounts.component.scss'],
  standalone: false,
})
export class EmailAccountsComponent implements OnInit, OnDestroy {
  constructor(
    private dialog: MatDialog,
    private location: LocationService,
    private route: ActivatedRoute,
    private campaignService: CampaignService,
    private notificationService: NotificationService,
  ) {}

  status: 'loading' | 'empty' | 'received' = 'loading';
  isValid: boolean;
  selectedEmail: EmailCredentialSummaryResponse | null = null;

  lockActionsSubject = new BehaviorSubject<boolean>(false);

  $list: Observable<any[]>;

  private $unsubscribe = new Subject<void>();

  ngOnInit(): void {
    this.updateList();
    this.handleQueryParams();
  }

  addAccount(): void {
    this.dialog
      .open(EmailAcountEditorModalComponent)
      .afterClosed()
      .pipe(
        takeUntil(this.$unsubscribe),
        filter((settings?: CampaignEmailCredential) => !!settings),
        switchMap((settings: CampaignEmailCredential) => {
          this.lockActionsSubject.next(true);

          return this.campaignService.saveEmailCredential(settings);
        }),
        tap(() => {
          this.updateList();
          this.notificationService.notify(
            NotificationTypeEnum.SUCCESS,
            'Account successfuly added.',
          );
        }),
        catchError(() => of(null)),
        finalize(() => {
          this.lockActionsSubject.next(false);
        }),
      )
      .subscribe();
  }

  deleteAccount(account: EmailCredentialSummaryResponse): void {
    const confimationModalData: UpdateEntity = {
      title: 'CAMPAIGN.SETTINGS.EMAIL_ACCOUNT.DELETE_CONFIRMATION',
      name: account.fromEmail,
    };

    this.dialog
      .open(RemoveEntityModalComponent, { data: confimationModalData })
      .afterClosed()
      .pipe(
        takeUntil(this.$unsubscribe),
        filter((deletionConfirmed: boolean) => deletionConfirmed),
        switchMap(() => {
          this.lockActionsSubject.next(true);
          return this.campaignService.deleteEmailCredential(account.id);
        }),
        tap(() => {
          this.updateList();
          this.notificationService.notify(
            NotificationTypeEnum.SUCCESS,
            'Account succesfully deleted',
          );
        }),
        catchError(() => of(null)),
        finalize(() => {
          this.lockActionsSubject.next(false);
        }),
      )
      .subscribe();
  }

  editAccount(account: EmailCredentialSummaryResponse): void {
    this.lockActionsSubject.next(true);

    this.campaignService
      .getEmailCredential(account.id)
      .pipe(
        takeUntil(this.$unsubscribe),
        switchMap((settings: CampaignEmailCredential) => {
          return this.dialog
            .open(EmailAcountEditorModalComponent, { data: settings })
            .afterClosed();
        }),
        filter((settings?: CampaignEmailCredential) => !!settings),
        switchMap((settings: CampaignEmailCredential) => {
          settings = { ...settings, id: account.id };
          return this.campaignService.updateEmailCredential(settings);
        }),
        tap(() => {
          this.updateList();
          this.notificationService.notify(
            NotificationTypeEnum.SUCCESS,
            'Settings succesfully updated.',
          );
        }),
        catchError(() => of(null)),
        finalize(() => {
          this.lockActionsSubject.next(false);
        }),
      )
      .subscribe();
  }

  selectPrimaryEmail(credendtial: EmailCredentialSummaryResponse) {
    this.campaignService
      .setMainEmailAccount(credendtial.id)
      .pipe(
        (takeUntil(this.$unsubscribe),
        catchError((error) => {
          this.notificationService.notify(
            NotificationTypeEnum.ERROR,
            'Something went wrong. Try again later',
          );
          return NEVER;
        })),
      )
      .subscribe(() => {
        this.selectedEmail = credendtial;
        this.notificationService.notify(
          NotificationTypeEnum.SUCCESS,
          'Now it is your primary email',
        );
      });
  }

  private handleQueryParams(): void {
    this.route.queryParams.pipe(takeUntil(this.$unsubscribe)).subscribe((params: Params) => {
      if ('addAccount' in params) {
        this.addAccount();
        this.location.replaceQuery('tab=email');
      }
    });
  }

  private updateList(): void {
    this.$list = this.campaignService.getEmailCredentials().pipe(
      tap((credentials: EmailCredentialSummaryResponse[]) => {
        this.status = credentials.length ? 'received' : 'empty';
        this.isValid = credentials.some((item: EmailCredentialSummaryResponse) => !item.isValid);
        this.selectedEmail = credentials.find(
          (item: EmailCredentialSummaryResponse) => item.mainAccount,
        );
      }),
    );
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }
}
